"use client"

import { useState, useEffect, useMemo } from "react"
import { motion, AnimatePresence } from "framer-motion"
import { ChevronLeft, ChevronRight } from "lucide-react"
import { Modal } from "@/components/ui/modal"
import { cn } from "@/lib/utils"
import type { TemplateItem } from "@/app/store/api/templateApi"

interface TemplatePreviewModalProps {
  template: TemplateItem | null
  isOpen: boolean
  onClose: () => void
  onUseTemplate?: (template: TemplateItem) => void
}

export function TemplatePreviewModal({
  template,
  isOpen,
  onClose,
  onUseTemplate,
}: TemplatePreviewModalProps) {
  const [currentSlide, setCurrentSlide] = useState(0)

  const slides = useMemo(() => {
    if (!template) return []
    
    const rawSlides = (template as any).slides;
    if (rawSlides && Array.isArray(rawSlides) && rawSlides.length > 0) {
      return rawSlides.map((s: any, idx: number) => ({
        slideType: s.slideType || `Slide ${idx + 1}`,
        variant: s.variant || null,
        thumbnailUrl: s.thumbnail || s.thumbnailUrl || template.thumbnailUrl
      }))
    }

    if (template.slideTemplates && template.slideTemplates.length > 0) {
      return template.slideTemplates
    }
    
    if (template.documentTemplate) {
      return [{
        slideType: "Document Preview",
        variant: null,
        thumbnailUrl: template.documentTemplate.thumbnailUrl || template.thumbnailUrl
      }]
    }
    
    return [{
      slideType: "Template Preview",
      variant: null,
      thumbnailUrl: template.thumbnailUrl
    }]
  }, [template])

  // Reset slide index when template changes
  useEffect(() => {
    setCurrentSlide(0)
  }, [template?._id])

  if (!template || slides.length === 0) return null

  const hasPrev = currentSlide > 0
  const hasNext = currentSlide < slides.length - 1

  const goToPrev = () => {
    if (hasPrev) setCurrentSlide((prev) => prev - 1)
  }

  const goToNext = () => {
    if (hasNext) setCurrentSlide((prev) => prev + 1)
  }

  const handleClose = () => {
    setCurrentSlide(0)
    onClose()
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      className="max-w-4xl w-[95vw] p-0 overflow-hidden flex flex-col shadow-2xl border-white/20 bg-white rounded-2xl md:rounded-3xl"
    >
      {/* Header */}
      <div className="px-5 md:px-6 pt-5 md:pt-6 pb-3 flex items-start justify-between gap-4">
        <div className="flex-1 min-w-0 pr-8">
          <h2 className="text-lg md:text-xl font-bold text-slate-900 tracking-tight">
            {template.name}
          </h2>
          {template.description && (
            <p className="mt-1 text-sm text-slate-500 leading-relaxed line-clamp-2">
              {template.description}
            </p>
          )}
        </div>
      </div>

      {/* Slideshow */}
      <div className="relative flex-1 px-5 md:px-6 pb-4">
        <div className="relative bg-slate-50 rounded-xl border border-slate-100 overflow-hidden">
          {/* Main slide display */}
          <div className="relative aspect-video w-full">
            <AnimatePresence mode="wait">
              <motion.img
                key={`${template._id}-${currentSlide}`}
                src={slides[currentSlide].thumbnailUrl || undefined}
                alt={`${slides[currentSlide].slideType} slide`}
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                transition={{ duration: 0.2 }}
                className="w-full h-full object-contain"
              />
            </AnimatePresence>

            {/* Navigation arrows */}
            {slides.length > 1 && (
              <>
                <button
                  onClick={goToPrev}
                  disabled={!hasPrev}
                  className={cn(
                    "absolute left-2 top-1/2 -translate-y-1/2 w-8 h-8 rounded-full bg-white/90 backdrop-blur-sm shadow-lg border border-slate-200 flex items-center justify-center transition-all cursor-pointer",
                    hasPrev
                      ? "hover:bg-white hover:scale-110 text-slate-700"
                      : "opacity-30 cursor-not-allowed text-slate-400"
                  )}
                >
                  <ChevronLeft className="w-4 h-4" />
                </button>
                <button
                  onClick={goToNext}
                  disabled={!hasNext}
                  className={cn(
                    "absolute right-2 top-1/2 -translate-y-1/2 w-8 h-8 rounded-full bg-white/90 backdrop-blur-sm shadow-lg border border-slate-200 flex items-center justify-center transition-all cursor-pointer",
                    hasNext
                      ? "hover:bg-white hover:scale-110 text-slate-700"
                      : "opacity-30 cursor-not-allowed text-slate-400"
                  )}
                >
                  <ChevronRight className="w-4 h-4" />
                </button>
              </>
            )}
          </div>

          {/* Slide counter */}
          {slides.length > 1 && (
            <div className="absolute bottom-3 left-1/2 -translate-x-1/2 px-3 py-1 bg-black/60 backdrop-blur-sm rounded-full text-white text-[11px] font-semibold">
              {currentSlide + 1} / {slides.length}
            </div>
          )}
        </div>

        {/* Thumbnail strip */}
        {slides.length > 1 && (
          <div className="flex gap-2 mt-3 overflow-x-auto pb-1" style={{ scrollbarWidth: "none" }}>
            {slides.map((slide: any, idx: number) => (
              <button
                key={`${slide.slideType}-${idx}`}
                onClick={() => setCurrentSlide(idx)}
                className={cn(
                  "flex-shrink-0 w-20 h-12 rounded-lg overflow-hidden border-2 transition-all cursor-pointer",
                  idx === currentSlide
                    ? "border-blue-500 shadow-md ring-2 ring-blue-200"
                    : "border-slate-200 hover:border-slate-300 opacity-70 hover:opacity-100"
                )}
              >
                <img
                  src={slide.thumbnailUrl || undefined}
                  alt={`${slide.slideType} thumbnail`}
                  className="w-full h-full object-cover"
                />
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Footer with action */}
      <div className="px-5 md:px-6 py-4 bg-slate-50 border-t border-slate-100 flex items-center justify-between gap-4">
        <div className="hidden md:block">
          <p className="text-xs font-bold text-slate-800 capitalize">
            {slides[currentSlide].slideType}
            {slides[currentSlide].variant ? ` - ${slides[currentSlide].variant}` : ""}
          </p>
          <p className="text-[10px] text-slate-400 font-medium">
            Slide {currentSlide + 1} of {slides.length}
          </p>
        </div>
        <button
          onClick={() => {
            onUseTemplate?.(template)
            handleClose()
          }}
          className="px-6 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-xl text-xs font-bold transition-all hover:scale-[1.02] active:scale-95 shadow-md shadow-blue-500/20 cursor-pointer w-full md:w-auto"
        >
          Use This Template
        </button>
      </div>
    </Modal>
  )
}
